import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CommissionCalculationResponse,
  CommissionRuleCreateRequest,
  CommissionRuleItem,
  CommissionRuleListQuery,
  CommissionRuleUpdateRequest,
} from '@crm/shared';
import { api } from '../../lib/api';

const BASE = '/commissions';

export const commissionsKeys = {
  all: ['commissions'] as const,
  rules: (filter: CommissionRuleListQuery) => ['commissions', 'rules', filter] as const,
  calculate: (userId: number, month: string) =>
    ['commissions', 'calculate', userId, month] as const,
};

export function useCommissionRules(filter: CommissionRuleListQuery) {
  return useQuery({
    queryKey: commissionsKeys.rules(filter),
    queryFn: async (): Promise<CommissionRuleItem[]> => {
      const params: Record<string, string | number> = {};
      if (filter.userId !== undefined) params.userId = filter.userId;
      if (filter.serviceId !== undefined) params.serviceId = filter.serviceId;
      if (filter.active) params.active = filter.active;
      const res = await api.get<CommissionRuleItem[]>(`${BASE}/rules`, { params });
      return res.data;
    },
  });
}

export function useCommissionRuleCreate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CommissionRuleCreateRequest): Promise<CommissionRuleItem> => {
      const res = await api.post<CommissionRuleItem>(`${BASE}/rules`, input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: commissionsKeys.all }),
  });
}

export function useCommissionRuleUpdate(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CommissionRuleUpdateRequest): Promise<CommissionRuleItem> => {
      const res = await api.patch<CommissionRuleItem>(`${BASE}/rules/${id}`, input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: commissionsKeys.all }),
  });
}

export function useCommissionRuleDelete() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await api.delete(`${BASE}/rules/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: commissionsKeys.all }),
  });
}

export function useCommissionCalculation(userId: number | null, month: string) {
  return useQuery({
    queryKey: commissionsKeys.calculate(userId ?? 0, month),
    queryFn: async (): Promise<CommissionCalculationResponse> => {
      const res = await api.get<CommissionCalculationResponse>(`${BASE}/calculate`, {
        params: { userId, month },
      });
      return res.data;
    },
    enabled: userId !== null && /^\d{4}-(0[1-9]|1[0-2])$/.test(month),
    staleTime: 30_000,
  });
}

/**
 * Pobiera CSV jako blob i wymusza download. Spójne z F13 (responseType blob,
 * URL.createObjectURL, trigger przez <a download>).
 */
export async function downloadCommissionsCsv(userId: number, month: string): Promise<void> {
  const res = await api.get<Blob>(`${BASE}/export`, {
    params: { userId, month },
    responseType: 'blob',
  });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = `prowizje_${userId}_${month}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
